'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useStore } from '@nanostores/react';
import { toast } from 'sonner';
import {
  Check,
  Download,
  FileText,
  File as FileIcon,
  Image as ImageIcon,
  MoreHorizontal,
  Paperclip,
  X,
} from 'lucide-react';

import type { JsonValue } from '@lotushealth/lotushealth_js_sdk/schemas';

import {
  deletePriorAuthAttachment,
  downloadPriorAuthAttachment,
  usePriorAuthActionMutation,
  usePriorAuthDetailsQuery,
  type PriorAuthActionBody,
  type PriorAuthorizationAnswerDetailT,
  type PriorAuthorizationAnsweredQuestionT,
  type PriorAuthorizationAnswerRequestT,
  type PriorAuthorizationChoiceAnswerT,
  type PriorAuthorizationQuestionT,
  type PriorAuthorizationStatusTypeT,
} from '@/entities/prior-auth';
import { usePharmacyByIdQuery, usePrescriptionQuery } from '@/entities/rx';
import { RxDraftSummaryNode } from '@/features/rx-form';
import { useProposedActionMutations } from '@/features/proposed-actions-plan/model/useProposedActionMutations';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Checkbox } from '@/shared/ui/shadcn/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { HoldToSubmit } from '@/shared/ui/HoldToSubmit';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/shadcn/tooltip';
import { closeCopilotOverlay } from '@/shared/stores/ui';
import { logger, type SourceProposedActionT } from '@/shared/lib';
import { useCopilotComposer } from '@/features/copilot/lib';

import {
  $lmnDraftStatus,
  clearLmnDraftStatusForCase,
  lmnAttachmentFileName,
} from '../model/lmnDraftStatus';
import type { PriorAuthActionPrefill } from '../model/priorAuthActionPrefill';

/** Strip the `data:<mime>;base64,` prefix that ``FileReader.readAsDataURL`` emits. */
function stripDataUrlPrefix(dataUrl: string): string {
  const commaIdx = dataUrl.indexOf(',');
  return commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
}

async function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected file reader result'));
        return;
      }
      resolve(stripDataUrlPrefix(result));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Attachment filename → Lucide icon. Just enough fidelity to visually
 * distinguish PDFs from images from "anything else" — DoseSpot doesn't
 * give us a content-type so we go off the extension.
 */
function attachmentIcon(fileName: string | null | undefined) {
  const name = (fileName ?? '').toLowerCase();
  if (name.endsWith('.pdf')) return FileText;
  if (/\.(png|jpe?g|gif|webp|bmp|tiff?|svg|heic)$/i.test(name)) return ImageIcon;
  return FileIcon;
}

/** Pretty short upload date for the attachment row, e.g. "May 1, 2026". */
function formatAttachmentDate(stamp: string | null | undefined): string | null {
  if (!stamp) return null;
  const parsed = new Date(stamp);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Hand a downloaded blob to the browser as a "Save As" prompt. Pulled
 * out so the entity-layer ``downloadPriorAuthAttachment`` (which only
 * knows about transport) stays free of DOM concerns.
 */
function triggerBrowserDownload(blob: Blob, suggestedFileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = suggestedFileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Defer revocation a tick so the browser can start the save.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}

function toJsonValueRecord(value: Record<string, unknown>): Record<string, JsonValue> {
  const result: Record<string, JsonValue> = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    if (entryValue !== null && entryValue !== undefined) {
      result[key] = entryValue as JsonValue;
    }
  });
  return result;
}

/** Render a single answer detail row as a plain string for the transcript. */
function formatAnswerDetail(detail: PriorAuthorizationAnswerDetailT): string {
  if (detail.Answer !== null && detail.Answer !== undefined && detail.Answer !== '') {
    return detail.Answer;
  }
  if (
    detail.AnswerFreeText !== null &&
    detail.AnswerFreeText !== undefined &&
    detail.AnswerFreeText !== ''
  ) {
    return detail.AnswerFreeText;
  }
  if (detail.AnswerNumeric !== null && detail.AnswerNumeric !== undefined) {
    return String(detail.AnswerNumeric);
  }
  if (detail.AnswerDateTime !== null && detail.AnswerDateTime !== undefined) {
    return String(detail.AnswerDateTime);
  }
  return '—';
}

/** Flatten all answered questions across every question set in the history. */
function collectAnsweredQuestions(
  questionSets: Array<
    { AnsweredQuestions?: PriorAuthorizationAnsweredQuestionT[] | null } | undefined
  >
): PriorAuthorizationAnsweredQuestionT[] {
  const flat: PriorAuthorizationAnsweredQuestionT[] = [];
  for (const set of questionSets) {
    for (const aq of set?.AnsweredQuestions ?? []) {
      flat.push(aq);
    }
  }
  return flat;
}

// DoseSpot `CurrentStatus` values that mean the case is closed — no more
// submit / cancel / attachment actions make sense. Appeal is still allowed
// from some of these via ``CanAppeal``.
const TERMINAL_STATUSES = new Set<PriorAuthorizationStatusTypeT>([
  'Approved',
  'Denied',
  'PartiallyDenied',
  'Canceled',
  'Closed',
  'Deleted',
  'NotRequired',
]);

// Payer-owned statuses where the clinician has no action to take — the case
// is in the payer's court and we're waiting for their next move.
const WAITING_STATUSES = new Set<PriorAuthorizationStatusTypeT>([
  'AppealRequested',
  'Pending',
  'Requested',
  'InProcess',
  'CancelRequested',
]);

/**
 * Wizard for acting on a Prior Authorization case.
 *
 * Conversation-style UI:
 * - Previously answered questions are shown as a greyed-out transcript at
 *   the top (payer's question + clinician's answer).
 * - Any currently pending question is rendered below as an editable form.
 * - The primary footer button ("Submit answer" while a question is pending,
 *   "Submit to payer" once the question set is exhausted) is disabled until
 *   the clinician has actually supplied a valid answer. Upload Attachment
 *   lives next to it as a secondary button so the clinician can drop in a
 *   PDF without leaving the flow.
 * - Appeal / Deny / Cancel are tucked into an overflow menu and only
 *   surfaced when the case state makes them meaningful (``CanAppeal``,
 *   offline cases, not-yet-closed cases).
 *
 * Driven by the ``prefill`` prop (delivered by the overlay). The prefill
 * always carries a ``recordId`` (our internal UUID) so we can fetch live case
 * details from ``usePriorAuthDetailsQuery`` — and a ``caseId`` so we can post
 * actions against DoseSpot without a second round-trip.
 */
interface PriorAuthActionWizardProps {
  prefill: PriorAuthActionPrefill;
}

export function PriorAuthActionWizard({ prefill }: PriorAuthActionWizardProps) {
  const mutations = useProposedActionMutations();
  const priorAuthActionMutation = usePriorAuthActionMutation();

  const activePrefill = prefill;
  const sourceProposedActionRef = useRef<SourceProposedActionT | null>(
    prefill.sourceProposedAction ??
      (prefill.action ? { externalId: prefill.action.external_id } : null)
  );

  const [submitting, setSubmitting] = useState(false);

  const [selectedChoices, setSelectedChoices] = useState<number[]>([]);
  const [freeTextAnswer, setFreeTextAnswer] = useState('');
  const [numericAnswer, setNumericAnswer] = useState('');
  const [dateAnswer, setDateAnswer] = useState('');

  const [appealOpen, setAppealOpen] = useState(false);
  const [appealReason, setAppealReason] = useState('');

  // ---------------------------------------------------------------------
  // "Generate LMN" button state (4-state machine)
  //
  // Almost entirely chat-driven: the ``draft_prior_auth_letter`` tool's
  // lifecycle is the source of truth for the ``drafting`` state, and
  // ``lmnAttached`` (filename match against case attachments) is the
  // source of truth for ``attached``. The only piece of local state is
  // ``awaitingDraftSinceSeq``: the lifecycle ``seq`` value at the moment
  // the clinician clicked the button. Without this baseline a *stale*
  // ``'done'`` event left over from a prior draft cycle would
  // immediately knock a fresh click back to ``idle-ready`` (the bug
  // that motivated this rewrite). Once we observe a lifecycle event
  // with ``seq`` strictly greater than the click baseline AND state
  // ``'done'`` / ``'error'``, drafting ends.
  //
  // The 90s wall-clock fallback is a safety net for the case where the
  // chat never fires a lifecycle event at all (e.g. websocket drop
  // before the tool starts streaming).
  //
  // Drafting fires *before* Submit-to-Payer so the resulting attachment
  // can ride along with the original ``/submit`` transmission. DoseSpot's
  // ``/submit`` is one-shot — anything attached after only goes out with
  // a future question round, if the payer asks for one.
  // ---------------------------------------------------------------------
  const [awaitingDraftSinceSeq, setAwaitingDraftSinceSeq] = useState<number | null>(null);
  const draftingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  // Hard-block dialog when DoseSpot reports `AttachmentRequired=true` on
  // the case but there are zero attachments uploaded. Distinct from
  // ``submitConfirmOpen`` (LMN-drafted-but-not-attached): that one is a
  // soft warning, this one is a stop. The dialog takes precedence over
  // the LMN guard so the clinician sees the payer-mandated requirement
  // first.
  const [attachmentRequiredDialogOpen, setAttachmentRequiredDialogOpen] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);

  const { sendText: sendCopilotText } = useCopilotComposer();

  const [formError, setFormError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const detailsQuery = usePriorAuthDetailsQuery(activePrefill?.recordId, {
    refetchInterval: 30_000,
  });

  // Tear down any in-flight drafting timer when the wizard unmounts.
  useEffect(() => {
    return () => {
      if (draftingTimerRef.current) {
        clearTimeout(draftingTimerRef.current);
        draftingTimerRef.current = null;
      }
    };
  }, []);

  const caseId = activePrefill?.caseId ?? 0;
  const recordId = activePrefill?.recordId ?? '';
  const patientUserId = activePrefill?.patientUserId;

  const dosespotPrescriptionId = useMemo(() => {
    if (activePrefill?.dosespotPrescriptionId) return activePrefill.dosespotPrescriptionId;
    const fromCase = detailsQuery.data?.case.PrescriptionId;
    return fromCase != null ? String(fromCase) : '';
  }, [activePrefill?.dosespotPrescriptionId, detailsQuery.data]);

  const prescriptionQuery = usePrescriptionQuery(patientUserId, dosespotPrescriptionId);

  const pharmacyId = prescriptionQuery.data?.PharmacyId
    ? String(prescriptionQuery.data.PharmacyId)
    : undefined;
  const pharmacyQuery = usePharmacyByIdQuery(pharmacyId);
  const pharmacyDisplayName = pharmacyQuery.data?.StoreName
    ? String(pharmacyQuery.data.StoreName)
    : undefined;

  const diagnosis = useMemo(() => {
    const primary = prescriptionQuery.data?.FirstPrescriptionDiagnosis?.PrimaryDiagnosis;
    if (!primary?.DiagnosisCode) return null;
    return {
      code: String(primary.DiagnosisCode),
      name: primary.DiagnosisDescription ? String(primary.DiagnosisDescription) : '',
    };
  }, [prescriptionQuery.data]);

  const pendingQuestion: PriorAuthorizationQuestionT | null = useMemo(() => {
    if (!detailsQuery.data) return null;
    return detailsQuery.data.pendingQuestion ?? null;
  }, [detailsQuery.data]);

  const pendingQuestionId = useMemo(() => {
    const raw = detailsQuery.data?.case.NextQuestionId;
    return typeof raw === 'number' ? raw : null;
  }, [detailsQuery.data]);

  const answeredQuestions = useMemo(() => {
    return collectAnsweredQuestions(detailsQuery.data?.history?.QuestionSets ?? []);
  }, [detailsQuery.data]);

  const answerType = pendingQuestion?.AnswerType ?? null;
  const currentStatus = detailsQuery.data?.case.CurrentStatus ?? null;
  const isTerminal = currentStatus !== null && TERMINAL_STATUSES.has(currentStatus);
  const isWaiting = currentStatus !== null && WAITING_STATUSES.has(currentStatus);
  // ``CanAppeal`` lives on ``case.FinalStatus`` as a bool, but the history
  // endpoint echoes it back as a "Yes"/"No" string. Support both.
  const canAppealFromCase = detailsQuery.data?.case.FinalStatus?.CanAppeal ?? null;
  const canAppealFromHistory = detailsQuery.data?.history?.CanAppeal ?? null;
  const canAppeal =
    canAppealFromCase === true ||
    (typeof canAppealFromHistory === 'string' && canAppealFromHistory.toLowerCase() === 'yes');
  const isOffline = detailsQuery.data?.case.IsOffline === true;
  const isExpedited = detailsQuery.data?.case.IsExpedited === true;

  const hasPendingQuestion = pendingQuestion !== null && pendingQuestionId !== null;

  // Subscribe to ``draft_prior_auth_letter`` lifecycle for our case. The
  // custom tool-part renderer publishes events into ``$lmnDraftStatus``.
  // We treat the chat as the source of truth: a lifecycle event with
  // ``seq`` strictly greater than the click baseline and a terminal
  // state (``done`` / ``error``) ends drafting.
  const lmnDraftStatusByCase = useStore($lmnDraftStatus);
  const lmnLifecycle = caseId > 0 ? (lmnDraftStatusByCase[caseId] ?? null) : null;
  const lmnLifecycleSeq = lmnLifecycle?.seq ?? 0;
  const lmnLifecycleState = lmnLifecycle?.state ?? null;
  useEffect(() => {
    if (awaitingDraftSinceSeq === null) return;
    // Ignore stale events from before the click — those are leftover
    // from a previous draft cycle and would otherwise instantly cancel
    // a fresh click that landed on a case which already had a ``done``
    // event recorded.
    if (lmnLifecycleSeq <= awaitingDraftSinceSeq) return;
    if (lmnLifecycleState === 'done' || lmnLifecycleState === 'error') {
      setAwaitingDraftSinceSeq(null);
      if (draftingTimerRef.current) {
        clearTimeout(draftingTimerRef.current);
        draftingTimerRef.current = null;
      }
    }
  }, [awaitingDraftSinceSeq, lmnLifecycleSeq, lmnLifecycleState]);

  // Filename-based detection: the LMN tool always uploads with the
  // canonical name ``lmn-pa-{caseId}.pdf`` (see
  // ``sign_and_attach_prior_auth_letter_tool.py``), so we look for that
  // exact name on the case. The wizard doesn't need to subscribe to a
  // bespoke attach lifecycle — ``SignAndAttachPriorAuthLetterToolCard``
  // invalidates ``priorAuthKeys.all`` directly when the AI's tool
  // completes, which causes ``detailsQuery`` to refetch and this
  // ``useMemo`` to re-evaluate against fresh ``Attachments`` data.
  const expectedLmnFileName = caseId > 0 ? lmnAttachmentFileName(caseId).toLowerCase() : null;
  const lmnAttached = useMemo(() => {
    if (!expectedLmnFileName) return false;
    const attachments = detailsQuery.data?.case.Attachments ?? [];
    return attachments.some((att) => (att.FileName ?? '').toLowerCase() === expectedLmnFileName);
  }, [detailsQuery.data, expectedLmnFileName]);

  // Mirror of DoseSpot's ``AttachmentRequired`` flag on the case. When
  // true the payer requires at least one supporting document before
  // ``/submit`` is honoured; we treat this as a hard precondition and
  // block the Submit-to-Payer hold until *some* attachment exists. We
  // intentionally don't restrict to LMN — any uploaded attachment
  // satisfies the flag (the LMN is just the most common case). The
  // DoseSpot ``/submit`` endpoint itself is documented as "submit
  // answered questions to payer" with no explicit reject behaviour for
  // a missing attachment, but the flag exists precisely to head off
  // downstream payer rejections, so we gate client-side.
  const caseRequiresAttachment = !!detailsQuery.data?.case.AttachmentRequired;
  const uploadedAttachmentCount = detailsQuery.data?.case.Attachments?.length ?? 0;
  const submitBlockedByMissingAttachment = caseRequiresAttachment && uploadedAttachmentCount === 0;

  // Drafting is "true" if either:
  //  (a) the clinician clicked and we haven't yet seen a post-click
  //      terminal lifecycle event, OR
  //  (b) we're observing a fresh in-flight ``draft_prior_auth_letter``
  //      tool call in chat (covers AI-initiated drafts the clinician
  //      didn't trigger via the button).
  const isDrafting =
    awaitingDraftSinceSeq !== null || (lmnLifecycleState === 'inflight' && lmnLifecycleSeq > 0);

  // Drives the submit-without-LMN guard. Chat-derived so it fires
  // whether the draft came from the button or the AI. Excludes
  // ``error`` — a failed draft means there's no LMN to attach, so the
  // "generated but not attached" dialog would be misleading.
  const hasGeneratedLmn = lmnLifecycle !== null && lmnLifecycle.state !== 'error';

  type LmnButtonStateT = 'idle-locked' | 'idle-ready' | 'drafting' | 'attached';
  const lmnButtonState: LmnButtonStateT = lmnAttached
    ? 'attached'
    : isDrafting
      ? 'drafting'
      : submitting || isTerminal || isWaiting || hasPendingQuestion
        ? 'idle-locked'
        : 'idle-ready';

  /** Whether the answer form is filled out well enough to submit. */
  const answerIsComplete = useMemo(() => {
    if (!pendingQuestion) return false;
    switch (answerType) {
      case 'Select':
      case 'Multiselect':
        return selectedChoices.length > 0;
      case 'FreeText':
        return freeTextAnswer.trim().length > 0;
      case 'Numeric':
        return Number.isFinite(Number(numericAnswer)) && numericAnswer.trim().length > 0;
      case 'Date':
      case 'DateTime':
        return dateAnswer.length > 0;
      default:
        return false;
    }
  }, [answerType, dateAnswer, freeTextAnswer, numericAnswer, pendingQuestion, selectedChoices]);

  // What the primary footer button does right now — either answer the
  // open question or submit the completed question set back to the payer.
  const primaryMode: 'answer_question' | 'submit' = hasPendingQuestion
    ? 'answer_question'
    : 'submit';
  // Once the case has moved into a waiting status the answer/submit work
  // is done; we lock the button to a "Submitted to payer" pill so the
  // clinician sees the finalised state instead of an inert
  // "Submit to payer" that confusingly looks pressable.
  // Two different submit semantics share the same hold-to-submit gesture:
  //  - ``answer_question``: each press commits one answer to DoseSpot.
  //    Labelled "Hold to answer" so it's clear this is *one* round-trip,
  //    not the final payer transmission.
  //  - ``submit``: this is the one-shot ``/submit`` that ships the whole
  //    answered question set to the payer. Labelled "Submit to payer"
  //    (the hold gesture itself communicates "this is destructive";
  //    keeping the label terse keeps the footer readable).
  const primaryLabel = isWaiting
    ? 'Submitted to payer'
    : submitting
      ? 'Submitting…'
      : primaryMode === 'answer_question'
        ? 'Hold to answer'
        : 'Submit to payer';
  // ``submitConfirmOpen`` and ``attachmentRequiredDialogOpen`` are
  // included so the ``HoldToSubmit`` button is inert while either
  // safety dialog is showing. Without this, a keyboard-focused user
  // (or one whose pointer events bleed through the modal backdrop)
  // can complete a second hold gesture; the next call into
  // ``handlePrimaryWithLmnGuard`` then sees the dialog already open,
  // shortcuts past the guard (``!submitConfirmOpen`` flips to false),
  // and submits without the LMN — silently defeating exactly the
  // check the dialog exists to enforce. The hard-block dialog has the
  // same shape: a second hold while it's open could chain into the
  // soft warning and end up calling ``handlePrimary`` even though the
  // payer-mandated attachment is still missing.
  const primaryDisabled =
    submitting ||
    isTerminal ||
    isWaiting ||
    submitConfirmOpen ||
    attachmentRequiredDialogOpen ||
    (primaryMode === 'answer_question' && !answerIsComplete);

  const handleFilePick = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!caseId) {
        setFormError('Missing case id for this prior auth.');
        return;
      }
      try {
        const b64 = await readFileAsBase64(file);
        setSubmitting(true);
        setFormError(null);
        await priorAuthActionMutation.mutateAsync({
          caseId,
          request: {
            action: 'upload_attachment',
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            contentBase64: b64,
          },
          recordId: recordId || undefined,
          patientUserId,
        });
        toast.success('Attachment uploaded.', { description: file.name });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setFormError(`Attachment upload failed: ${message}`);
        logger.error('priorAuth.uploadAttachment.failed', { err });
      } finally {
        setSubmitting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [caseId, patientUserId, priorAuthActionMutation, recordId]
  );

  const buildAnswerPayload = useCallback((): PriorAuthorizationAnswerRequestT | null => {
    if (!pendingQuestion) return null;
    const payload: PriorAuthorizationAnswerRequestT = {};

    switch (answerType) {
      case 'Select':
      case 'Multiselect': {
        const choices = (pendingQuestion.AnswerChoices ?? []).filter(
          (c): c is typeof c & { QuestionChoiceId: number } =>
            typeof c.QuestionChoiceId === 'number' && selectedChoices.includes(c.QuestionChoiceId)
        );
        if (choices.length === 0) return null;
        const selected: PriorAuthorizationChoiceAnswerT[] = choices.map((c) => ({
          PriorAuthorizationQuestionChoiceId: c.QuestionChoiceId,
          PriorAuthorizationQuestionChoiceText:
            freeTextAnswer.trim() && c.QuestionChoiceText ? c.QuestionChoiceText : null,
        }));
        payload.PriorAuthorizationQuestionChoices = selected;
        if (freeTextAnswer.trim()) {
          const anyAllowsFreeText = choices.some(
            (c) => c.AdditionalFreeTextAllowed || c.AdditionalFreeTextRequired
          );
          if (anyAllowsFreeText) {
            payload.FreeTextAnswer = freeTextAnswer.trim();
          }
        }
        return payload;
      }
      case 'FreeText': {
        if (!freeTextAnswer.trim()) return null;
        payload.FreeTextAnswer = freeTextAnswer.trim();
        return payload;
      }
      case 'Numeric': {
        const parsed = Number(numericAnswer);
        if (!Number.isFinite(parsed)) return null;
        payload.NumericAnswer = parsed;
        return payload;
      }
      case 'Date':
      case 'DateTime': {
        if (!dateAnswer) return null;
        payload.DateTimeAnswer = dateAnswer;
        return payload;
      }
      default:
        return null;
    }
  }, [answerType, dateAnswer, freeTextAnswer, numericAnswer, pendingQuestion, selectedChoices]);

  const resetAnswerInputs = useCallback(() => {
    setSelectedChoices([]);
    setFreeTextAnswer('');
    setNumericAnswer('');
    setDateAnswer('');
  }, []);

  /** Run the primary action: either answer the current question or submit. */
  const handlePrimary = useCallback(async () => {
    if (!caseId) {
      setFormError('Missing case id for this prior auth.');
      return;
    }

    setFormError(null);
    setSubmitting(true);

    try {
      let req: PriorAuthActionBody;
      if (primaryMode === 'answer_question') {
        if (!pendingQuestionId) {
          setFormError('No pending question to answer.');
          return;
        }
        const answer = buildAnswerPayload();
        if (!answer) {
          setFormError('Please provide an answer before submitting.');
          return;
        }
        req = { action: 'answer_question', questionId: pendingQuestionId, answer };
      } else {
        req = { action: 'submit' };
      }

      const response = await priorAuthActionMutation.mutateAsync({
        caseId,
        request: req,
        recordId: recordId || undefined,
        patientUserId,
      });

      // Answer chains: DoseSpot sometimes issues the PA question set one at
      // a time, driven by branching logic on prior answers. When the server
      // echoes back a ``nextQuestionId`` we keep the wizard open — the
      // mutation already invalidates the details query above, so the new
      // ``pendingQuestion`` refetches on the next tick and the form
      // re-renders with fresh choices.
      const hasMoreQuestions =
        primaryMode === 'answer_question' &&
        response.nextQuestionId !== null &&
        response.nextQuestionId !== undefined;

      const source = sourceProposedActionRef.current;
      if (source && !hasMoreQuestions) {
        const toolOutput = toJsonValueRecord({
          caseId,
          action: primaryMode,
          nextQuestionId: response.nextQuestionId ?? null,
        });
        await mutations.finalizeFromSource(source, toolOutput).catch((err: unknown) => {
          logger.error('priorAuth.finalizeFromSource.failed', {
            externalId: source.externalId,
            err,
          });
        });
      }

      if (hasMoreQuestions) {
        toast.success('Answer recorded. Next question loaded.');
        resetAnswerInputs();
        setFormError(null);
      } else if (primaryMode === 'answer_question') {
        toast.success('Answer recorded. Ready to submit to payer.');
        resetAnswerInputs();
      } else {
        toast.success('Prior auth submitted to payer.');
        // The LMN draft is *not* fired here — it's fired earlier from the
        // explicit "Generate LMN" button in the footer, while the case is
        // still in ``ActionRequired`` and the clinician can still sign &
        // attach the result. Firing on submit-to-payer would race the
        // payer transmission (DoseSpot's ``/submit`` is one-shot) and any
        // attachment added afterward only ever rides along with a future
        // question round.
        // Don't auto-close: the clinician should see the wizard transition
        // to the locked "Submitted to payer" state so they have a clear
        // visual confirmation that the case is now with the payer.
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setFormError(`Action failed: ${message}`);
      logger.error('priorAuth.submit.failed', { mode: primaryMode, err });
    } finally {
      setSubmitting(false);
    }
  }, [
    buildAnswerPayload,
    caseId,
    mutations,
    patientUserId,
    pendingQuestionId,
    primaryMode,
    priorAuthActionMutation,
    recordId,
    resetAnswerInputs,
  ]);

  /** Click handler for the footer "Generate LMN" button. Sends a chat
   *  message that nudges the copilot to call ``draft_prior_auth_letter``
   *  for this case, then optimistically flips the button into
   *  ``drafting`` state by stamping the current lifecycle ``seq`` as
   *  the click baseline. Anything strictly past that seq drives the
   *  return to ``idle-ready``; the 90s timer is the safety net for the
   *  case where chat never fires a single lifecycle event. */
  const handleGenerateLmn = useCallback(() => {
    if (!caseId) return;
    if (lmnButtonState !== 'idle-ready') return;
    setAwaitingDraftSinceSeq(lmnLifecycleSeq);
    if (draftingTimerRef.current) clearTimeout(draftingTimerRef.current);
    const timerCaseId = caseId;
    draftingTimerRef.current = setTimeout(() => {
      setAwaitingDraftSinceSeq(null);
      // ``isDrafting`` ORs the local baseline with the lifecycle
      // atom's ``inflight`` state. If the chat side fired
      // ``inflight`` and then the tool hung (websocket drop, LLM
      // stall), the local baseline goes away when this timer fires
      // but the atom entry would otherwise stay ``inflight``
      // forever — leaving the button stuck on "Drafting…" with no
      // escape short of a page reload. Drop the atom entry too so
      // the button can return to ``idle-ready``.
      clearLmnDraftStatusForCase(timerCaseId);
      draftingTimerRef.current = null;
    }, 90_000);
    sendCopilotText({
      text: `Draft a Letter of Medical Necessity for prior authorization case #${caseId}.`,
      source: 'prior-auth-wizard:generate-lmn',
    });
  }, [caseId, lmnButtonState, lmnLifecycleSeq, sendCopilotText]);

  /** Wraps ``handlePrimary`` for the Submit-to-Payer hold. Two guards
   *  layer on top of the base submit, in priority order:
   *
   *  1. **Attachment-required hard block.** If DoseSpot's case carries
   *     ``AttachmentRequired=true`` but no attachments have been
   *     uploaded yet, open the hard-block dialog and bail. Hard
   *     because the payer's expecting documentation; letting the
   *     clinician push past would only surface as a payer denial
   *     later. This guard always wins over the LMN soft warning so
   *     the dominant constraint is what the clinician sees.
   *  2. **LMN soft warning.** If the clinician kicked off an LMN draft
   *     but never attached it, surface a confirm dialog so they don't
   *     accidentally transmit without the letter.
   *
   *  ``answer_question`` submits skip both guards — they only gate the
   *  final ``/submit`` round-trip.
   *
   *  The actual submit is performed by ``handlePrimary`` either inline
   *  (when both guards pass) or via ``handleConfirmSubmit`` (when the
   *  clinician chooses "Submit anyway" on the LMN warning). The
   *  attachment-required dialog has no override path — the only way
   *  through is to upload an attachment and try again. */
  const handlePrimaryWithLmnGuard = useCallback(() => {
    if (
      primaryMode === 'submit' &&
      submitBlockedByMissingAttachment &&
      !attachmentRequiredDialogOpen
    ) {
      setAttachmentRequiredDialogOpen(true);
      return;
    }
    if (primaryMode === 'submit' && hasGeneratedLmn && !lmnAttached && !submitConfirmOpen) {
      setSubmitConfirmOpen(true);
      return;
    }
    void handlePrimary();
  }, [
    attachmentRequiredDialogOpen,
    handlePrimary,
    hasGeneratedLmn,
    lmnAttached,
    primaryMode,
    submitBlockedByMissingAttachment,
    submitConfirmOpen,
  ]);

  const handleConfirmSubmit = useCallback(() => {
    setSubmitConfirmOpen(false);
    // Drop the lifecycle entry for this case once the clinician has
    // explicitly acknowledged submitting without the LMN. The
    // ``$lmnDraftStatus`` atom is module-level and otherwise persists
    // across wizard resets, so a follow-up payer question that
    // re-enters ``ActionRequired`` would re-fire this same guard with
    // the (now-stale) ``done`` lifecycle still set — forcing the
    // clinician to ack the same warning every round trip. Per-case
    // clear keeps the lifecycle around for *other* cases the
    // clinician may have in flight.
    if (caseId) clearLmnDraftStatusForCase(caseId);
    void handlePrimary();
  }, [caseId, handlePrimary]);

  /** One-off terminal actions from the overflow menu (cancel / deny / appeal). */
  const runSimpleAction = useCallback(
    async (req: PriorAuthActionBody, successToast: string) => {
      if (!caseId) {
        setFormError('Missing case id for this prior auth.');
        return;
      }
      setFormError(null);
      setSubmitting(true);
      try {
        await priorAuthActionMutation.mutateAsync({
          caseId,
          request: req,
          recordId: recordId || undefined,
          patientUserId,
        });

        const source = sourceProposedActionRef.current;
        if (source) {
          const toolOutput = toJsonValueRecord({ caseId, action: req.action });
          await mutations.finalizeFromSource(source, toolOutput).catch((err: unknown) => {
            logger.error('priorAuth.finalizeFromSource.failed', {
              externalId: source.externalId,
              err,
            });
          });
        }

        toast.success(successToast);
        closeCopilotOverlay('priorAuthAction');
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setFormError(`Action failed: ${message}`);
        logger.error('priorAuth.simpleAction.failed', { action: req.action, err });
      } finally {
        setSubmitting(false);
      }
    },
    [caseId, mutations, patientUserId, priorAuthActionMutation, recordId]
  );

  const handleAppealConfirm = useCallback(async () => {
    if (!appealReason.trim()) {
      setFormError('Appeal reason is required.');
      return;
    }
    await runSimpleAction({ action: 'appeal', appealReason: appealReason.trim() }, 'Appeal filed.');
    setAppealOpen(false);
    setAppealReason('');
  }, [appealReason, runSimpleAction]);

  const toggleChoice = (choiceId: number) => {
    if (answerType === 'Select') {
      setSelectedChoices([choiceId]);
      return;
    }
    setSelectedChoices((prev) =>
      prev.includes(choiceId) ? prev.filter((id) => id !== choiceId) : [...prev, choiceId]
    );
  };

  // Which of the destructive/terminal actions are surfaced right now?
  const showCancel = !isTerminal;
  const showDeny = !isTerminal && isOffline;
  const showAppeal = canAppeal;
  const hasOverflowActions = showCancel || showDeny || showAppeal;

  if (!activePrefill) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No prior authorization selected.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold">Prior Authorization — Case #{caseId}</div>
        {currentStatus ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {currentStatus}
          </span>
        ) : null}
        {isExpedited ? (
          <span className="rounded-full border border-orange-400/50 bg-orange-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-orange-700 dark:border-orange-500/30 dark:bg-orange-950/40 dark:text-orange-300">
            Expedited
          </span>
        ) : null}
      </div>

      {detailsQuery.isLoading ? (
        <div className="text-xs text-muted-foreground">Loading case details…</div>
      ) : null}

      {detailsQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load case</AlertTitle>
          <AlertDescription>
            {detailsQuery.error instanceof Error
              ? detailsQuery.error.message
              : 'Unknown error loading case.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {formError ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">{formError}</AlertDescription>
        </Alert>
      ) : null}

      {prescriptionQuery.data ? (
        <section
          aria-label="Prescription"
          className="flex-none rounded-md border border-border bg-muted/30 p-2.5"
        >
          <RxDraftSummaryNode
            className="text-xs [&>*]:space-y-1"
            medicationDisplayName={prescriptionQuery.data.DisplayName ?? '—'}
            directions={prescriptionQuery.data.Directions ?? '—'}
            quantity={String(prescriptionQuery.data.Quantity ?? '')}
            refills={String(prescriptionQuery.data.Refills ?? '')}
            daysSupply={String(prescriptionQuery.data.DaysSupply ?? '')}
            pharmacyDisplayName={pharmacyDisplayName}
            pharmacyNotes={
              prescriptionQuery.data.PharmacyNotes
                ? String(prescriptionQuery.data.PharmacyNotes)
                : undefined
            }
            noSubstitutions={prescriptionQuery.data.NoSubstitutions === true}
            diagnosis={diagnosis}
          />
        </section>
      ) : prescriptionQuery.isLoading ? (
        <div className="text-xs text-muted-foreground">Loading prescription…</div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
        {answeredQuestions.length > 0 ? (
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Answered questions
            </Label>
            <ol className="space-y-2">
              {answeredQuestions.map((aq, idx) => (
                <li
                  key={`${idx}-${aq.Question ?? 'q'}`}
                  className="rounded-md border border-border bg-muted/30 p-2 opacity-70"
                >
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                      <Check className="h-3 w-3" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-xs font-medium leading-relaxed whitespace-pre-wrap">
                        {aq.Question ?? '—'}
                      </p>
                      {(aq.Answers ?? []).length > 0 ? (
                        <ul className="space-y-0.5">
                          {(aq.Answers ?? []).map((ans, aIdx) => (
                            <li
                              key={aIdx}
                              className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap"
                            >
                              {formatAnswerDetail(ans)}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">No answer recorded.</p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ) : null}

        {isWaiting ? (
          <Alert>
            <AlertDescription className="text-xs">
              This case is waiting for payer review ({currentStatus}).
            </AlertDescription>
          </Alert>
        ) : null}

        {hasPendingQuestion && pendingQuestion ? (
          <div className="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-2.5">
            <Label className="text-[11px] font-medium uppercase tracking-wide text-primary">
              Pending question
            </Label>
            <p className="text-xs font-medium leading-relaxed whitespace-pre-wrap">
              {pendingQuestion.QuestionText ?? '—'}
            </p>

            {(answerType === 'Select' || answerType === 'Multiselect') &&
            pendingQuestion.AnswerChoices ? (
              <div className="space-y-2">
                <Label className="text-xs">
                  {answerType === 'Select' ? 'Choose one' : 'Choose one or more'}
                </Label>
                <div className="flex flex-col gap-2">
                  {pendingQuestion.AnswerChoices.map((choice) => {
                    const id = choice.QuestionChoiceId ?? -1;
                    const checked = selectedChoices.includes(id);
                    return (
                      <label key={id} className="flex items-start gap-2 text-xs leading-relaxed">
                        <Checkbox checked={checked} onCheckedChange={() => toggleChoice(id)} />
                        <span>{choice.QuestionChoiceText ?? '—'}</span>
                      </label>
                    );
                  })}
                </div>
                <div>
                  <Label className="text-xs">Additional notes (optional)</Label>
                  <Textarea
                    value={freeTextAnswer}
                    onChange={(e) => setFreeTextAnswer(e.target.value)}
                    rows={2}
                    className="text-xs"
                  />
                </div>
              </div>
            ) : null}

            {answerType === 'FreeText' ? (
              <div>
                <Label className="text-xs">Answer</Label>
                <Textarea
                  value={freeTextAnswer}
                  onChange={(e) => setFreeTextAnswer(e.target.value)}
                  rows={3}
                  className="text-xs"
                />
              </div>
            ) : null}

            {answerType === 'Numeric' ? (
              <div>
                <Label className="text-xs">Answer (numeric)</Label>
                <Input
                  type="number"
                  value={numericAnswer}
                  onChange={(e) => setNumericAnswer(e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
            ) : null}

            {answerType === 'Date' || answerType === 'DateTime' ? (
              <div>
                <Label className="text-xs">
                  {answerType === 'Date' ? 'Answer (date)' : 'Answer (date & time)'}
                </Label>
                <Input
                  type={answerType === 'Date' ? 'date' : 'datetime-local'}
                  value={dateAnswer}
                  onChange={(e) => setDateAnswer(e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
            ) : null}

            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Submitting will commit your answer to DoseSpot — answers can&apos;t be revised once
              submitted.
            </p>
          </div>
        ) : !detailsQuery.isLoading && !isTerminal && !isWaiting ? (
          <div className="space-y-1.5 rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
            <p>
              All questions are answered. Hold the button below to send this case (and any
              attachments) to the payer.
            </p>
            <p className="text-[11px] leading-relaxed">
              Once submitted, attachments lock until/unless the payer requests follow-up info. If
              you&apos;re drafting an LMN, sign and attach it before submitting.
            </p>
          </div>
        ) : null}

        {isTerminal ? (
          <Alert>
            <AlertDescription className="text-xs">
              {canAppeal
                ? `This case was ${currentStatus?.toLowerCase()}. You can file an appeal using the menu below.`
                : `This case is closed (${currentStatus}). No further answers can be submitted.`}
            </AlertDescription>
          </Alert>
        ) : null}

        {appealOpen ? (
          <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2.5">
            <Label className="text-xs font-medium">Appeal reason</Label>
            <Textarea
              value={appealReason}
              onChange={(e) => setAppealReason(e.target.value)}
              rows={3}
              className="text-xs"
              placeholder="Explain why this case should be reconsidered…"
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAppealOpen(false);
                  setAppealReason('');
                  setFormError(null);
                }}
                className="text-xs"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAppealConfirm}
                className="text-xs"
                disabled={submitting || appealReason.trim().length === 0}
              >
                File appeal
              </Button>
            </div>
          </div>
        ) : null}

        {(detailsQuery.data?.case.Attachments ?? []).length > 0 ? (
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Attachments
            </Label>
            <ul className="space-y-2">
              {(detailsQuery.data?.case.Attachments ?? []).map((att, idx) => {
                const fileName = att.FileName ?? `attachment-${att.AttachmentId ?? idx}`;
                const Icon = attachmentIcon(fileName);
                const uploaded = formatAttachmentDate(att.CreatedDateStamp ?? null);
                const attachmentIdRaw = att.AttachmentId;
                const attachmentId =
                  typeof attachmentIdRaw === 'number'
                    ? attachmentIdRaw
                    : typeof attachmentIdRaw === 'string'
                      ? Number(attachmentIdRaw)
                      : null;
                const canDownload =
                  caseId > 0 && attachmentId !== null && !Number.isNaN(attachmentId);
                return (
                  <li
                    key={`${att.AttachmentId ?? idx}-${fileName}`}
                    className="rounded-md border border-border bg-muted/30 p-2"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium leading-tight">{fileName}</p>
                        {uploaded ? (
                          <p className="text-[11px] leading-tight text-muted-foreground">
                            Uploaded {uploaded}
                          </p>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        disabled={!canDownload}
                        onClick={() => {
                          if (!canDownload || attachmentId === null) return;
                          downloadPriorAuthAttachment(caseId, attachmentId)
                            .then((blob) => {
                              triggerBrowserDownload(
                                blob,
                                fileName || `pa-${caseId}-attachment-${attachmentId}`
                              );
                            })
                            .catch((err: unknown) => {
                              const msg = err instanceof Error ? err.message : 'Unknown error';
                              toast.error('Could not download attachment', {
                                description: msg,
                              });
                              logger.error('priorAuth.attachment.download.failed', {
                                caseId,
                                attachmentId,
                                err,
                              });
                            });
                        }}
                        title="Download"
                        aria-label={`Download ${fileName}`}
                      >
                        <Download className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                        // Mirror the Attach button's gating — delete is only
                        // permitted while the case is clinician-owned
                        // (ActionRequired). Past Submit-to-Payer the case is
                        // payer-owned and any deletion would only hide the
                        // file from our copy, not retract what was already
                        // transmitted.
                        disabled={
                          !canDownload ||
                          submitting ||
                          isTerminal ||
                          isWaiting ||
                          deletingAttachmentId === attachmentId
                        }
                        onClick={() => {
                          if (!canDownload || attachmentId === null) return;
                          setDeletingAttachmentId(attachmentId);
                          deletePriorAuthAttachment(caseId, attachmentId)
                            .then(async () => {
                              toast.success('Attachment removed.', { description: fileName });
                              await detailsQuery.refetch();
                            })
                            .catch((err: unknown) => {
                              const msg = err instanceof Error ? err.message : 'Unknown error';
                              // DoseSpot sometimes refuses post-submit deletes;
                              // we don't pretend the row is gone in that case.
                              toast.error('Could not remove attachment', { description: msg });
                              logger.error('priorAuth.attachment.delete.failed', {
                                caseId,
                                attachmentId,
                                err,
                              });
                            })
                            .finally(() => {
                              setDeletingAttachmentId((current) =>
                                current === attachmentId ? null : current
                              );
                            });
                        }}
                        title="Remove"
                        aria-label={`Remove ${fileName}`}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="flex flex-none items-center justify-between gap-2 border-t border-border pt-2">
        <div className="flex items-center gap-1">
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => void handleFilePick(e)}
            className="sr-only"
            aria-label="Upload attachment"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="text-xs"
            // Attach is only enabled while the case is in clinician-owned
            // status (i.e. ``ActionRequired``). Once we hit Submit-to-Payer
            // the case transitions into a waiting/payer-owned status, and
            // anything attached after that wouldn't ride along with the
            // already-transmitted answer set anyway — DoseSpot's
            // ``/submit`` is one-shot. If the payer comes back with
            // follow-up questions the case re-enters ActionRequired and
            // this button reactivates.
            disabled={submitting || isTerminal || isWaiting}
            title="Upload attachment"
          >
            <Paperclip className="mr-1 h-3.5 w-3.5" aria-hidden />
            Attach
          </Button>
          {(() => {
            // 4-state button: ``idle-locked`` (questions outstanding),
            // ``idle-ready`` (clickable), ``drafting`` (LLM in flight),
            // ``attached`` (an attachment was added since the click).
            const label =
              lmnButtonState === 'drafting'
                ? 'Drafting…'
                : lmnButtonState === 'attached'
                  ? 'LMN attached'
                  : 'Generate LMN';
            const button = (
              <Button
                size="sm"
                variant="outline"
                onClick={handleGenerateLmn}
                disabled={lmnButtonState !== 'idle-ready'}
                className="ml-2 text-xs"
                aria-label="Generate Letter of Medical Necessity"
              >
                {lmnButtonState === 'attached' ? (
                  <Check className="mr-1 h-3.5 w-3.5" aria-hidden />
                ) : null}
                {label}
              </Button>
            );
            // Only attach a tooltip while the button is locked because
            // questions are still outstanding — that's the only state
            // where "Generate LMN" is disabled with a clinician-actionable
            // reason. ``drafting`` and ``attached`` are self-explanatory;
            // ``idle-locked`` from terminal/waiting/submitting needs no
            // explanation either (the rest of the wizard is locked too).
            //
            // The disabled ``<button>`` itself doesn't dispatch pointer
            // events, so Radix's Tooltip can't hook hover on it directly
            // — wrap it in a ``<span>`` (with ``inline-flex`` so it
            // doesn't disturb the flex layout) so hover bubbles up.
            if (lmnButtonState === 'idle-locked' && hasPendingQuestion) {
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">{button}</span>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start" sideOffset={6}>
                    Answer all payer questions to enable LMN drafting.
                  </TooltipContent>
                </Tooltip>
              );
            }
            return button;
          })()}
          {hasOverflowActions ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs"
                  disabled={submitting}
                  title="More actions"
                  aria-label="More actions"
                >
                  <MoreHorizontal className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top">
                {showAppeal ? (
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      setAppealOpen(true);
                      setFormError(null);
                    }}
                  >
                    Appeal…
                  </DropdownMenuItem>
                ) : null}
                {showDeny ? (
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      void runSimpleAction({ action: 'deny' }, 'Case marked denied.');
                    }}
                  >
                    Deny
                  </DropdownMenuItem>
                ) : null}
                {showCancel ? (
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={(e) => {
                      e.preventDefault();
                      void runSimpleAction({ action: 'cancel' }, 'Case cancelled.');
                    }}
                  >
                    Cancel case
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => closeCopilotOverlay('priorAuthAction')}
            className="text-xs"
            disabled={submitting}
          >
            Close
          </Button>
          <HoldToSubmit
            onSubmit={handlePrimaryWithLmnGuard}
            disabled={primaryDisabled}
            label={primaryLabel}
          />
        </div>
      </div>

      {attachmentRequiredDialogOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pa-attachment-required-title"
        >
          <div className="mx-4 w-full max-w-sm space-y-3 rounded-md border border-border bg-background p-4 shadow-lg">
            <h3 id="pa-attachment-required-title" className="text-sm font-semibold">
              Attachment required
            </h3>
            <p className="text-xs leading-relaxed text-muted-foreground">
              The payer requires supporting documentation for this prior authorization before it can
              be submitted. Upload an attachment — typically a Letter of Medical Necessity — and try
              again.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                size="sm"
                onClick={() => setAttachmentRequiredDialogOpen(false)}
                className="text-xs"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      ) : submitConfirmOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lmn-submit-confirm-title"
        >
          <div className="mx-4 w-full max-w-sm space-y-3 rounded-md border border-border bg-background p-4 shadow-lg">
            <h3 id="lmn-submit-confirm-title" className="text-sm font-semibold">
              Submit without an LMN?
            </h3>
            <p className="text-xs leading-relaxed text-muted-foreground">
              You generated a Letter of Medical Necessity but haven&apos;t attached it to this case.
              The payer won&apos;t see the draft unless you sign and attach it before submitting.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSubmitConfirmOpen(false)}
                className="text-xs"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmSubmit}
                className="text-xs"
                disabled={submitting}
              >
                Submit anyway
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
